import {Observable, of} from 'rxjs';
import {map, mergeMap, tap} from 'rxjs/operators';

/*
 * Safely sets an item in sessionStorage.  Catching any exceptions.
 * Don't use this method if it's critical to know the store has succeeded.
 */
export function sessionSetItemSafely(key: string, data) {
  try {
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch(e) {
    console.error('Unable to store item in session storage', e);
  }
}

/**
 * Will return an Observable that either returns the item from sessionStorage, or returns the passed Observable with an added pipe to set the sessionStorage.
 */
export function fromSessionOr<T>(cacheKey: string, report$: Observable<T>): Observable<T> {

  //If we're to use the cache, pull it from the sessionStorage
  return of(sessionStorage.getItem(cacheKey))
    .pipe(
      map(o => o ? JSON.parse(o) : null),
      // tap(o => console.debug(`From session: ${JSON.stringify(o)}`)),
      mergeMap(o => {
        if(o) {
          return of(o);
        } else {
          //get it from the backend and store it in the cache for possible future use (user refreshes screen)
          return report$.pipe(tap(report => sessionSetItemSafely(cacheKey, report)));
        }
      })
    );
}

/**
 * If the passed item exists, store it in the sessionStorage and return it back, along with a false to indicate that it was not retrieved from the sessionStorage.
 * If the passed item does not exist, grab it from the sessionStorage and return it, along with a true to indicate that it was retrieved from the sessionStorage.
 *
 * This method is used as a helper for components on page refresh.  Their expected router state will be null, so they check the sessionStorage if it's there.  If it is, it returns
 * it with a true.  The true is later used to know if subsequent expensive data should also already exist in the sessionStorage or if it needs to be fetched from the backend.
 */
export function sessionStoreOrGetIfNull<T>(cacheKey: string, originalInput: T): [boolean, T] {
  if(originalInput) {
    sessionSetItemSafely(cacheKey, originalInput);
    return [false, originalInput];
  } else {
    return [true, JSON.parse(sessionStorage.getItem(cacheKey))];
  }
}
